import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client as ClientModel } from '../../models/client';
import { deleteClient } from '../../services/api/clientServices';
import { AppCard } from '../app/appCard';
import { AppConfirmationDelete } from '../app/confirmation/appConfiramationDelete';
import { AppConfirmationSuccessful } from '../app/confirmation/appConfirmationSuccessful';
import { useUserRoles } from '../../hooks/useUserRoles';

interface Props {
  client: ClientModel;
  onRequestSuccessful(): void;
  handleEditClient(client: ClientModel): void;
}

export function Client({
  client,
  onRequestSuccessful,
  handleEditClient,
}: Props): JSX.Element {
  const navigate = useNavigate();

  const { isAdmin } = useUserRoles();
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);
  const [isOpenSuccessful, setIsOpenSuccessful] = useState<boolean>(false);

  const handleDelete = useCallback(() => {
    setIsOpenConfirmation(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    deleteClient(client.id)
      .then(() => {
        setIsOpenConfirmation(false);
        setIsOpenSuccessful(true);
      })
      .then(() => {
        setTimeout(() => {
          setIsOpenSuccessful(false);
          onRequestSuccessful();
        }, 500);
      });
  }, [client.id, onRequestSuccessful]);

  const handleCloseConfirmation = useCallback(() => {
    setIsOpenConfirmation(false);
  }, []);

  const handleCloseSuccessful = useCallback(() => {
    setIsOpenSuccessful(false);
  }, []);

  const handleEdit = useCallback(() => {
    handleEditClient(client);
  }, [handleEditClient, client]);

  const handleProjects = useCallback(() => {
    navigate(`/projects/client/${client.id}`, {
      state: { client, projectsComingFromClient: true },
    });
  }, [client]);

  const clientActionsButtons = useMemo(() => {
    const baseActions = [
      {
        label: 'Projects',
        onClick: handleProjects,
        isPrimary: false,
      },
    ];

    if (isAdmin) {
      return [
        {
          label: 'Delete',
          onClick: handleDelete,
          isPrimary: false,
        },
        ...baseActions,
        {
          label: 'Edit',
          onClick: handleEdit,
          isPrimary: true,
        },
      ];
    }

    return baseActions;
  }, [isAdmin, handleProjects, handleDelete, handleEdit]);

  return (
    <>
      <AppConfirmationDelete
        isOpen={isOpenConfirmation}
        message={`Delete client ${client.name}?`}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmDelete}
      />

      <AppConfirmationSuccessful
        isOpen={isOpenSuccessful}
        onClose={handleCloseSuccessful}
        message="Successfully deleted"
      />

      <AppCard
        title={client.name}
        statusInfo={' '}
        details={[client.description]}
        buttons={clientActionsButtons}
      />
    </>
  );
}
